import { FC } from 'react';
import { Box, Breadcrumbs, Link } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Link as RouterLink } from 'react-router-dom';
import { RouteMap } from '@/routes/Routes';
import { useOrganizations } from '@/features/betaOrganizations/hooks/useOrganizations';
import { useOrganizationFolders } from '@/features/betaOrganizations/hooks/useOrganizationFolders';
import { BETA_ORGANIZATIONS_FEATURE_ENABLED } from '@/features/betaOrganizations/config';

interface TeamNoteBreadcrumbProps {
  noteId?: string;
}

/**
 * Vimeo-style breadcrumb shown at the top of a note that lives in a team:
 * "Team > Folder". The note's own name is intentionally omitted — it's already
 * rendered as the title directly below. Renders nothing for notes that aren't in
 * any team. Team/folder membership is resolved by which org/folder lists the
 * note id — notes don't carry organization_id/folder_id themselves.
 */
const TeamNoteBreadcrumb: FC<TeamNoteBreadcrumbProps> = ({ noteId }) => {
  const { organizations } = useOrganizations(BETA_ORGANIZATIONS_FEATURE_ENABLED);
  const team = noteId ? organizations.find((org) => org.note_ids?.includes(noteId)) : undefined;
  const { folders } = useOrganizationFolders(team?.id, BETA_ORGANIZATIONS_FEATURE_ENABLED && !!team);
  const folder = noteId ? folders.find((f) => f.note_ids?.includes(noteId)) : undefined;

  if (!team) return null;

  const teamPath = `${RouteMap.ORGANIZATIONS}/${team.id}`;

  return (
    <Box width="100%" maxWidth="1620px" mb={1}>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="team note breadcrumb">
        <Link component={RouterLink} to={teamPath} underline="hover" color="inherit">
          {team.name}
        </Link>
        {folder && (
          <Link component={RouterLink} to={`${teamPath}?folder=${folder.id}`} underline="hover" color="inherit">
            {folder.name}
          </Link>
        )}
      </Breadcrumbs>
    </Box>
  );
};

export default TeamNoteBreadcrumb;
